// THE Browser pane (Locations ⇄ Systems ⇄ Model tabs) — one component, every viewer.
//
// Extracted from AutodeskViewer's inline pane (the only viewer that had one) and now rendered by
// all four surfaces: the RVT viewer, the IFC viewer and the App-level panorama + 2D panes. The
// markup/styling is a 1:1 copy of the original inline render; the change that makes it shareable
// is that viewer-coupled actions (go to the element, highlight a room, highlight a group, draw a
// room) are OPTIONAL callbacks. Every button self-gates on its callback's presence, which is how
// per-viewer capabilities fall out WITHOUT forking the component: a panorama/2D host simply omits
// what it cannot do, and `focusTarget` makes the go-to action say what it will really do there.
// Tree DATA comes from the shared builders (buildPOIHierarchy, wired by usePOIHierarchies); the
// pane's own width and every offset that must clear it live in paneLayout.ts. Tab + expansion are
// internal state.

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSiteWriteAccess } from '../../hooks/useSiteWriteAccess';
import { CATEGORY_ICONS, getIconForCategory } from '../../constants/Categories';
import { HierarchyNode, collectPOIsFromNode } from './buildPOIHierarchy';
import { buildElementActions, ELEMENT_FOCUS_ACTIONS, ElementFocusTarget } from '../shared/elementActions';
import { showErrorToast, showSuccessToast } from '../shared/toast';
import {
  LocationRow,
  ExtractRoomPolygonsFn,
  fetchLocationTree,
  flattenLocations,
  fetchAvailableLocationProperties,
  fetchAvailableSystemProperties,
  fetchAssignablePOIs,
  exportRoomsCSV as runExportRoomsCSV,
  reassignLocations as runReassignLocations,
  reassignSystems as runReassignSystems,
  assignByGeolocation as runAssignByGeolocation,
  createLocation as runCreateLocation,
  saveManualStorey as runSaveManualStorey,
  mergeStoreys as runMergeStoreys,
  deleteLocation as runDeleteLocation,
  deleteAllLocations as runDeleteAllLocations,
  createPOIInLocation as runCreatePOIInLocation,
  assignExistingPOIToLocation as runAssignExistingPOI,
  removePOIFromLocation as runRemovePOIFromLocation,
  formatReassignResult,
  formatGeolocationResult,
} from '../../services/locationSystemActions';

// Group-highlight uses the DT secondary accent (cyan) — matches the original inline pane.
const GROUP_HIGHLIGHT_COLOR = '#48CAE4';

export interface POIHierarchyBrowserProps {
  locationsHierarchy: HierarchyNode[];
  systemsHierarchy: HierarchyNode[];
  /** Whether there is any data to show (POIs or custom rooms). Drives the empty state. */
  hasData: boolean;
  /** Empty-state content when hasData is false. */
  emptyMessage?: React.ReactNode;
  /** Initial tab (uncontrolled). Defaults to 'locations'. */
  defaultTab?: 'locations' | 'systems';
  /** Max height of the scrolling tree container. */
  maxHeight?: string;

  // ── Capability callbacks (each button self-gates on presence) ────────────────
  /** Click a POI row → focus/navigate to it (Show-in-3D on 3D viewers, locate on 2D, etc.). */
  onFocusPOI?: (poi: any) => void;
  /**
   * What `onFocusPOI` actually does on THIS surface, which is what the row and its ⋯ menu
   * promise in words: 'model3d' → "Show in 3D", 'panorama' → "Jump to nearest station",
   * 'map2d' → "Locate on map". Wording lives in the shared element-action vocabulary.
   */
  focusTarget?: ElementFocusTarget;
  /** "Room" button on a space with a room element (roomDbId). */
  onHighlightRoom?: (node: HierarchyNode) => void;
  /**
   * Open a system as its diagram. EVERY system can be shown as one (migration 053) — the
   * diagram row is created on first open — so this chip appears on every system node, not
   * only on those that already have a canvas. A host that cannot show diagrams omits the
   * callback and the chip disappears, per this pane's capability convention.
   */
  onOpenSystemDiagram?: (node: HierarchyNode) => void;
  /** "Room" button on a custom (drawn) room with polygon geometry. */
  onHighlightCustomRoom?: (node: HierarchyNode) => void;
  /** "Assets"/"3D" group button on any grouping node → highlight all POIs beneath it. */
  onHighlightGroup?: (pois: any[], color: string) => void;

  // ── Management actions taken from LocationsPage, appended to this browser ─────
  // Each is optional and self-gates, so a viewer/consumer that can't do it shows nothing.
  /** Start drawing a custom room (optionally inside a storey node). */
  onDrawRoom?: (storeyId?: string) => void;
  /** Start drawing a custom storey. */
  onDrawStorey?: () => void;
  /** Edit an existing custom room/storey node. */
  onEditCustom?: (node: HierarchyNode) => void;
  /** Open the viewer's existing search/bulk-edit panel (search + Set Systems/Locations + Reassign). */
  onOpenSearch?: () => void;
  /** Bulk assignment actions taken from the pages (async; button shows a busy state). */
  onReassignLocations?: () => void | Promise<void>;
  onAssignByGeolocation?: () => void | Promise<void>;
  onReassignSystems?: () => void | Promise<void>;
  onExportRoomsCSV?: () => void;

  // ── Built-in management (the LocationsPage/SystemsPage abilities, in-pane) ─────
  // Supplying `assetId` turns on the pane's own API-backed actions (services/
  // locationSystemActions): property-picker reassignment, geolocation assignment, rooms CSV,
  // location/storey CRUD and per-room asset wiring. Every form renders INLINE inside this
  // pane — no extra panel, no extra toggle. The legacy callbacks above still win when given,
  // so existing consumers keep their behavior unchanged.
  /** Asset id; enables the built-in management actions. */
  assetId?: string;
  /** Edit an existing custom STOREY node (host switches the viewer to storey-drawing mode). */
  onEditCustomStorey?: (location: any) => void;
  /** Open the P&ID / diagrams page (Systems tab). */
  onOpenDiagrams?: () => void;
  // ── Per-asset actions (same vocabulary as the viewer's double-click element menu) ─
  /** Open a ticket for this asset (host builds the service-call draft). */
  onCreateTicketForPOI?: (poi: any) => void;
  /**
   * Show the tickets that already reference this asset (service_call_pois, migration 043 —
   * GET /assets/:id/service-calls?poiId=). Offered next to "Create ticket" because the
   * first honest question about a broken asset is whether it has already been reported.
   */
  onViewTicketsForPOI?: (poi: any) => void;
  /**
   * Attach or create an IoT device bound to this asset (host opens the shared Add-IoT sheet
   * via useAddIoTDevice). Every row here IS an asset, so this row menu never needs the
   * create-the-asset-first branch the viewers' element menus carry.
   */
  onAddIoTForPOI?: (poi: any) => void;
  /** Open the P&ID page on the diagram node bound to this asset. */
  onShowPOIInDiagram?: (poi: any) => void;
  /**
   * Edit / delete one asset. Self-gating like every other handler here: a host that cannot do
   * it passes nothing and the item is simply absent, which is how the panorama and 2D-map
   * instances of this pane differ from the two 3D viewers without forking the component.
   */
  onEditPOI?: (poi: any) => void;
  onDeletePOI?: (poi: any) => void;
  /** Viewer-side room-polygon extractor, used by the geolocation assignment flow. */
  onRequestExtractPolygons?: ExtractRoomPolygonsFn;
  /** Fired after any mutation so the host can reload its POIs / custom rooms. */
  onDataChanged?: () => void;

  // ── Model tab ────────────────────────────────────────────────────────────────
  /**
   * Body of the third ("Model") tab — the host viewer's own model/extraction chrome
   * (loaded models + available-to-load + Extract POIs + phase filter). Supplied as a node so
   * this component stays viewer-agnostic; the tab only appears when a host provides it, and the
   * content renders unconditionally (no collapsing) whenever that tab is active.
   */
  viewerSections?: React.ReactNode;

  // ── Icon / label helpers (optional; faithful defaults match the Autodesk pane) ─
  getTypeIcon?: (poi: any) => string;
  getPOIType?: (poi: any) => string;
  getCategoryIcon?: (name: string) => string;
  getSubCategoryIcon?: (name: string) => string;
}

const defaultGetPOIType = (poi: any): string =>
  poi.poi_type_name || poi.ifc_type_name || poi.type || 'Unknown';

const defaultGetCategoryIcon = (category: string): string => {
  if (category === 'Unassigned') return '❓';
  return CATEGORY_ICONS[category] || '📦';
};

const defaultGetSubCategoryIcon = (name: string): string => {
  const icon = getIconForCategory(name);
  return icon === '📦' ? '🔧' : icon;
};

const groupBtnStyle: React.CSSProperties = {
  padding: '2px 6px',
  fontSize: '9px',
  backgroundColor: 'var(--dt-accent)',
  color: '#000',
  border: 'none',
  borderRadius: '3px',
  cursor: 'pointer',
  marginLeft: '4px',
};

// Toolbar buttons for the management actions appended from the pages.
const toolBtn: React.CSSProperties = {
  padding: '5px 9px',
  fontSize: '10px',
  fontWeight: 600,
  backgroundColor: 'var(--dt-accent)',
  color: '#000',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};
const busyStyle = (isBusy: boolean): React.CSSProperties => ({
  ...toolBtn,
  opacity: isBusy ? 0.6 : 1,
  cursor: isBusy ? 'wait' : 'pointer',
});

// ── Styles for the in-pane management forms (the pane is only ~320px wide, so every
//    form is a compact inline sheet stacked above the tree — never a floating panel).
const ghostBtn: React.CSSProperties = {
  ...toolBtn,
  backgroundColor: 'transparent',
  color: 'var(--dt-text-2)',
  border: '1px solid var(--dt-border)',
};
const nodeBtn: React.CSSProperties = {
  ...groupBtnStyle,
  backgroundColor: 'transparent',
  color: 'var(--dt-text-3)',
  border: '1px solid var(--dt-border)',
};
const nodeDangerBtn: React.CSSProperties = { ...nodeBtn, color: 'var(--dt-bad)' };
const panelStyle: React.CSSProperties = {
  backgroundColor: 'var(--dt-surface-3)',
  border: '1px solid var(--dt-border)',
  borderRadius: '6px',
  padding: '8px',
  marginBottom: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '4px 6px',
  fontSize: '11px',
  backgroundColor: 'var(--dt-surface)',
  color: 'var(--dt-text)',
  border: '1px solid var(--dt-border)',
  borderRadius: '4px',
};
const labelStyle: React.CSSProperties = {
  fontSize: '9px',
  color: 'var(--dt-text-3)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

// ── Redesigned pane chrome (tabs / filter row / Manage sheet / row ⋯ menus) ───
// Proportions and roles follow the approved DT mockup: three tabs, one quiet filter+Manage row,
// a sheet that drops below it, and per-row menus rendered INLINE under their row.
const tabBtnStyle = (active: boolean, grow: boolean): React.CSSProperties => ({
  flex: grow ? 1 : '0 0 auto',
  padding: grow ? '6px 10px' : '6px 12px',
  borderRadius: '8px',
  border: `1px solid ${active ? 'var(--dt-accent)' : 'var(--dt-border)'}`,
  backgroundColor: active ? 'var(--dt-accent)' : 'var(--dt-surface)',
  color: active ? 'var(--dt-accent-ink)' : 'var(--dt-text-2)',
  cursor: 'pointer',
  fontSize: '11px',
  fontWeight: active ? 700 : 600,
});

const filterInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  boxSizing: 'border-box',
  padding: '5px 8px',
  fontSize: '11px',
  backgroundColor: 'var(--dt-surface-2)',
  color: 'var(--dt-text)',
  border: '1px solid var(--dt-border)',
  borderRadius: '8px',
};

const sheetStyle: React.CSSProperties = {
  border: '1px solid var(--dt-border-strong)',
  borderRadius: '10px',
  backgroundColor: 'var(--dt-surface-2)',
  overflow: 'hidden',
  marginBottom: '8px',
};
const sheetTitleStyle: React.CSSProperties = {
  fontSize: '9px',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--dt-text-3)',
  margin: '8px 8px 3px',
};
const sheetWrapStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '5px',
  padding: '0 8px 8px',
};
const sheetBtnStyle: React.CSSProperties = {
  padding: '3px 8px',
  fontSize: '10px',
  fontWeight: 600,
  backgroundColor: 'var(--dt-surface)',
  color: 'var(--dt-text)',
  border: '1px solid var(--dt-border-strong)',
  borderRadius: '7px',
  cursor: 'pointer',
};

const rowMenuStyle: React.CSSProperties = {
  backgroundColor: 'var(--dt-surface)',
  border: '1px solid var(--dt-border-strong)',
  borderRadius: '9px',
  boxShadow: 'var(--dt-sh-md)',
  padding: '4px',
  margin: '3px 0 7px',
  display: 'flex',
  flexDirection: 'column',
};
const rowMenuSepStyle: React.CSSProperties = {
  height: '1px',
  backgroundColor: 'var(--dt-border)',
  margin: '3px 6px',
};
const dotsBtnStyle: React.CSSProperties = {
  width: '20px',
  height: '20px',
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  borderRadius: '6px',
  backgroundColor: 'transparent',
  color: 'var(--dt-text-3)',
  cursor: 'pointer',
  fontSize: '13px',
  lineHeight: 1,
};
const countPillStyle: React.CSSProperties = {
  fontSize: '9px',
  fontWeight: 700,
  padding: '1px 6px',
  borderRadius: '6px',
  backgroundColor: 'var(--dt-surface-3)',
  color: 'var(--dt-text-3)',
  flexShrink: 0,
};

/** A row action rendered as a quiet chip (Room / Assets / 3D stay visible on the row). */
const RowChip: React.FC<{
  label: string;
  title: string;
  onClick: (event: React.MouseEvent) => void;
}> = ({ label, title, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        flexShrink: 0,
        padding: '1px 6px',
        fontSize: '9px',
        fontWeight: 600,
        borderRadius: '999px',
        border: '1px solid var(--dt-border)',
        backgroundColor: hover ? 'var(--dt-accent-tint)' : 'transparent',
        color: hover ? 'var(--dt-accent-strong)' : 'var(--dt-text-3)',
        cursor: 'pointer',
      }}
    >{label}</button>
  );
};

/** One entry inside a row's ⋯ menu (hover follows the mockup's .bpmi). */
const RowMenuItem: React.FC<{
  label: string;
  title?: string;
  danger?: boolean;
  onClick: () => void;
}> = ({ label, title, danger, onClick }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      title={title || label}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'start',
        padding: '5px 9px',
        fontSize: '11px',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: hover ? 'var(--dt-surface-2)' : 'transparent',
        color: danger ? 'var(--dt-bad)' : (hover ? 'var(--dt-text)' : 'var(--dt-text-2)'),
        cursor: 'pointer',
      }}
    >{label}</button>
  );
};

/** Menu entries: an action, or a separator line. */
type RowMenuEntry = { label: string; title?: string; danger?: boolean; onClick: () => void } | 'sep';

/** One button inside the Manage sheet. */
interface SheetAction {
  label: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

/**
 * Filter a tree by node name (case-insensitive). A matching node keeps its whole subtree; a
 * non-matching node survives only if a descendant matches, and its id is reported so the caller
 * can auto-expand it. Ancestors are therefore always visible above their matches.
 */
function filterHierarchy(nodes: HierarchyNode[], needle: string, autoOpen: string[]): HierarchyNode[] {
  const out: HierarchyNode[] = [];
  for (const node of nodes) {
    if ((node.name || '').toLowerCase().includes(needle)) { out.push(node); continue; }
    const kids = filterHierarchy(node.children || [], needle, autoOpen);
    if (kids.length > 0) {
      autoOpen.push(node.id);
      out.push({ ...node, children: kids });
    }
  }
  return out;
}

/**
 * One flat bag for every in-pane form. A discriminated union would need a cast at each
 * field update; `kind` plus optional fields keeps the patch helper trivially type-safe.
 */
interface BrowserFormState {
  kind: 'reassign-locations' | 'reassign-systems' | 'create-location' | 'manual-storey' | 'merge-storeys' | 'add-asset';
  name?: string;
  description?: string;
  bottom?: string;
  top?: string;
  storeyProperty?: string;
  spaceProperty?: string;
  systemProperty?: string;
  subcategoryLevels?: string[];
  selected?: string[];
  destroyOrigins?: boolean;
  location?: LocationRow;
  step?: 'choice' | 'create' | 'assign';
  type?: string;
  category?: string;
  existingId?: string;
}

// POI categories offered when creating an asset from a room (matches LocationsPage's picker).
const POI_CATEGORY_OPTIONS = ['structure', 'room', 'system', 'safety', 'utility'];

export const POIHierarchyBrowser: React.FC<POIHierarchyBrowserProps> = ({
  locationsHierarchy,
  systemsHierarchy,
  hasData,
  emptyMessage,
  defaultTab = 'locations',
  maxHeight = 'calc(100vh - 450px)',
  onFocusPOI,
  focusTarget = 'model3d',
  onHighlightRoom,
  onOpenSystemDiagram,
  onHighlightCustomRoom,
  onHighlightGroup,
  onDrawRoom,
  onDrawStorey,
  onEditCustom,
  onOpenSearch,
  onReassignLocations,
  onAssignByGeolocation,
  onReassignSystems,
  onExportRoomsCSV,
  assetId,
  onEditCustomStorey,
  onOpenDiagrams,
  onCreateTicketForPOI,
  onViewTicketsForPOI,
  onAddIoTForPOI,
  onShowPOIInDiagram,
  onEditPOI,
  onDeletePOI,
  onRequestExtractPolygons,
  onDataChanged,
  viewerSections,
  getTypeIcon,
  getPOIType,
  getCategoryIcon,
  getSubCategoryIcon,
}) => {
  const [tab, setTab] = useState<'locations' | 'systems' | 'model'>(defaultTab);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  // Pane chrome: the filter query, the Manage sheet and the single open row ⋯ menu.
  const [filterText, setFilterText] = useState('');
  const [manageOpen, setManageOpen] = useState(false);
  const [openRowMenu, setOpenRowMenu] = useState<string | null>(null);

  /** Tab switch resets the sheet and closes any open row menu (mockup's bpTab). */
  const selectTab = useCallback((next: 'locations' | 'systems' | 'model') => {
    setTab(next);
    setManageOpen(false);
    setOpenRowMenu(null);
  }, []);

  /** Only one row menu is ever open. */
  const toggleRowMenu = useCallback((key: string) => {
    setOpenRowMenu(prev => (prev === key ? null : key));
  }, []);

  const runBusy = useCallback(async (key: string, fn: () => void | Promise<void>) => {
    setBusy(key);
    try { await fn(); } finally { setBusy(null); }
  }, []);

  // ── Built-in management state ────────────────────────────────────────────────
  // Two conditions, and the second one was missing: the pane must be given a site, AND the
  // person must be allowed to change it. `manage` drove the whole ⋯ sheet (draw a room, add
  // a storey, reassign, delete all locations) and every row menu, for everyone who could
  // OPEN the site — so a read-only member was handed the full editing vocabulary and got a
  // 403 from each item. The Locations page has always asked the right question
  // (LocationsPage.tsx: permission_level === 'write'); this asks the same one.
  const canWriteSite = useSiteWriteAccess(assetId);
  const manage = !!assetId && canWriteSite;
  const [locationTree, setLocationTree] = useState<LocationRow[]>([]);
  const [treeVersion, setTreeVersion] = useState(0);
  const [form, setForm] = useState<BrowserFormState | null>(null);
  const [result, setResult] = useState<{ text: string; tone: 'good' | 'bad' } | null>(null);
  const [propertyOptions, setPropertyOptions] = useState<string[]>([]);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [poiOptions, setPoiOptions] = useState<any[]>([]);

  // The location tree is what LocationsPage renders from — it is what makes the rooms CSV
  // byte-identical and what maps a tree node back to its `locations` row (for delete/edit).
  useEffect(() => {
    if (!assetId) { setLocationTree([]); return; }
    let cancelled = false;
    fetchLocationTree(assetId)
      .then(rows => { if (!cancelled) setLocationTree(rows); })
      .catch(err => console.warn('[POIHierarchyBrowser] Failed to load locations:', err));
    return () => { cancelled = true; };
  }, [assetId, treeVersion]);

  const locationsFlat = useMemo(() => flattenLocations(locationTree), [locationTree]);
  const storeyLocations = useMemo(
    () => locationsFlat.filter(l => l.type === 'Building Storey'),
    [locationsFlat],
  );

  // Tree nodes are keyed by NAME (they are grouped from POI fields), so mapping one back to a
  // `locations` row is a name lookup. An ambiguous name resolves to nothing rather than to a
  // guess — a destructive action must never fire against the wrong row.

  /** Tree storey node → its `locations` row (custom storeys carry is_custom). */
  const storeyLocationFor = useCallback((node: HierarchyNode): LocationRow | undefined => {
    const matches = storeyLocations.filter(l => l.name === node.name);
    return matches.length === 1 ? matches[0] : undefined;
  }, [storeyLocations]);

  /** Tree space node → its `locations` row. Custom rooms encode the id in the node id. */
  const spaceLocationFor = useCallback((
    node: HierarchyNode,
    parentStorey?: LocationRow,
  ): LocationRow | undefined => {
    const custom = /^custom-room-(.+)$/.exec(String(node.id));
    if (custom) {
      return locationsFlat.find(l => String(l.id) === custom[1])
        || ({ id: custom[1], name: node.name, type: 'Spaces', is_custom: true } as LocationRow);
    }
    const matches = locationsFlat.filter(l => l.type === 'Spaces' && l.name === node.name);
    if (matches.length === 1) return matches[0];
    // Same room name on several storeys — disambiguate by the storey we are rendering under.
    return parentStorey
      ? matches.find(l => String(l.parent_id) === String(parentStorey.id))
      : undefined;
  }, [locationsFlat]);

  const patchForm = useCallback((p: Partial<BrowserFormState>) => {
    setForm(f => (f ? { ...f, ...p } : f));
  }, []);

  const closeForm = useCallback(() => { setForm(null); setPropertyFilter(''); }, []);

  /** Reload the pane's own location tree AND tell the host to reload POIs / custom rooms. */
  const afterMutation = useCallback(() => {
    setTreeVersion(v => v + 1);
    if (onDataChanged) onDataChanged();
  }, [onDataChanged]);

  /**
   * Run a mutation with a busy state, a compact in-pane result line and the toast UX the
   * pages use. Returning a string from `fn` shows it in the result banner.
   */
  const runAction = useCallback(async (
    key: string,
    fn: () => Promise<string | void>,
    opts: { keepForm?: boolean; refresh?: boolean } = {},
  ) => {
    setBusy(key);
    setResult(null);
    try {
      const message = await fn();
      if (message) setResult({ text: message, tone: 'good' });
      if (!opts.keepForm) closeForm();
      if (opts.refresh !== false) afterMutation();
    } catch (error: any) {
      const text = error?.message || 'Action failed';
      setResult({ text, tone: 'bad' });
      showErrorToast(text);
    } finally {
      setBusy(null);
    }
  }, [afterMutation, closeForm]);

  // ── Form openers (each loads whatever list the form needs) ───────────────────
  const openReassignLocations = useCallback(async () => {
    setResult(null);
    setPropertyFilter('');
    setForm({ kind: 'reassign-locations', storeyProperty: '', spaceProperty: '' });
    if (!assetId) return;
    setBusy('props');
    try {
      const props = await fetchAvailableLocationProperties(assetId);
      setPropertyOptions(props);
      // "Level" is the usual storey property — preselect it exactly like LocationsPage.
      if (props.includes('Level')) patchForm({ storeyProperty: 'Level' });
    } catch (error) {
      setPropertyOptions([]);
    } finally {
      setBusy(null);
    }
  }, [assetId, patchForm]);

  const openReassignSystems = useCallback(async () => {
    setResult(null);
    setPropertyFilter('');
    setForm({ kind: 'reassign-systems', systemProperty: '', subcategoryLevels: [''] });
    if (!assetId) return;
    setBusy('props');
    try {
      const { properties } = await fetchAvailableSystemProperties(assetId);
      setPropertyOptions(properties);
    } catch (error) {
      setPropertyOptions([]);
    } finally {
      setBusy(null);
    }
  }, [assetId]);

  const loadPoiOptions = useCallback(async () => {
    if (!assetId) return;
    setBusy('pois');
    try {
      setPoiOptions(await fetchAssignablePOIs(assetId));
    } catch (error) {
      setPoiOptions([]);
    } finally {
      setBusy(null);
    }
  }, [assetId]);

  /**
   * Open the per-room "add asset" form. `step` lets a row menu jump straight to the branch the
   * user picked ("Add asset" → the create/assign choice, "Assign existing asset" → the picker);
   * both branches are the exact same form the "+ Asset" button opened.
   */
  const openAddAsset = useCallback(async (
    location: LocationRow,
    step: 'choice' | 'create' | 'assign' = 'choice',
  ) => {
    setResult(null);
    setForm({
      kind: 'add-asset', location, step,
      name: '', description: '', type: '', category: 'structure', existingId: '',
    });
    if (step === 'assign') loadPoiOptions();
  }, [loadPoiOptions]);

  // ── Built-in action handlers ─────────────────────────────────────────────────
  const doExportRoomsCSV = useCallback(() => {
    if (!runExportRoomsCSV(locationTree)) {
      setResult({ text: 'No rooms to export.', tone: 'bad' });
    }
  }, [locationTree]);

  const doGeolocate = useCallback(() => runAction('geo', async () => {
    const stats = await runAssignByGeolocation(assetId!, onRequestExtractPolygons);
    return formatGeolocationResult(stats);
  }), [assetId, onRequestExtractPolygons, runAction]);

  const doDeleteLocation = useCallback((location: LocationRow) => {
    if (!confirm(`Delete "${location.name}" and all its children?`)) return;
    runAction('del', async () => {
      await runDeleteLocation(assetId!, String(location.id));
      showSuccessToast('Location deleted');
      return `Deleted "${location.name}".`;
    });
  }, [assetId, runAction]);

  const doDeleteAllLocations = useCallback(() => {
    if (!confirm('Delete ALL locations for this asset? This cannot be undone.')) return;
    runAction('delAll', async () => {
      await runDeleteAllLocations(assetId!);
      showSuccessToast('All locations deleted successfully');
      return 'All locations deleted.';
    });
  }, [assetId, runAction]);

  const doRemovePOIFromLocation = useCallback((poi: any) => {
    if (!confirm('Remove this asset from this location?')) return;
    runAction('rmPoi', async () => {
      await runRemovePOIFromLocation(assetId!, poi);
      showSuccessToast('Asset removed from location successfully!');
      return 'Asset removed from location.';
    });
  }, [assetId, runAction]);

  // Property lists can run to hundreds of entries; the filter narrows every select at once.
  const filteredProperties = useMemo(() => {
    const needle = propertyFilter.trim().toLowerCase();
    return needle ? propertyOptions.filter(p => p.toLowerCase().includes(needle)) : propertyOptions;
  }, [propertyOptions, propertyFilter]);

  const toggle = useCallback((nodeId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  // ── Name filter over the active tree (empty query = the tree exactly as given) ─
  const needle = filterText.trim().toLowerCase();
  const filteredLocations = useMemo(() => {
    if (!needle) return { nodes: locationsHierarchy, autoOpen: [] as string[] };
    const autoOpen: string[] = [];
    return { nodes: filterHierarchy(locationsHierarchy, needle, autoOpen), autoOpen };
  }, [locationsHierarchy, needle]);
  const filteredSystems = useMemo(() => {
    if (!needle) return { nodes: systemsHierarchy, autoOpen: [] as string[] };
    const autoOpen: string[] = [];
    return { nodes: filterHierarchy(systemsHierarchy, needle, autoOpen), autoOpen };
  }, [systemsHierarchy, needle]);

  // Ancestors of a match open themselves so the match is reachable without any clicking.
  const autoExpanded = useMemo(
    () => new Set(tab === 'systems' ? filteredSystems.autoOpen : filteredLocations.autoOpen),
    [tab, filteredSystems, filteredLocations],
  );
  const isOpen = useCallback(
    (nodeId: string) => expanded.has(nodeId) || autoExpanded.has(nodeId),
    [expanded, autoExpanded],
  );

  const poiType = getPOIType ?? defaultGetPOIType;
  const typeIcon = getTypeIcon ?? ((poi: any) => {
    const icon = getIconForCategory(poiType(poi));
    return icon === '📦' ? '📍' : icon;
  });
  const categoryIcon = getCategoryIcon ?? defaultGetCategoryIcon;
  const subCategoryIcon = getSubCategoryIcon ?? defaultGetSubCategoryIcon;

  const focusPOI = (poi: any) => { if (poi && onFocusPOI) onFocusPOI(poi); };

  /** "Copy id" on an asset row copies the asset's own id (its handle in every API/table). */
  const copyAssetId = (poi: any) => {
    const text = String(poi?.id ?? '');
    if (!text || !navigator.clipboard?.writeText) {
      showErrorToast('Clipboard is not available in this browser');
      return;
    }
    navigator.clipboard.writeText(text)
      .then(() => showSuccessToast(`Copied id ${text}`))
      .catch(() => showErrorToast('Failed to copy id'));
  };
  const highlightGroup = (node: HierarchyNode) => {
    if (!onHighlightGroup) return;
    const pois = collectPOIsFromNode(node);
    if (pois.length > 0) onHighlightGroup(pois, GROUP_HIGHLIGHT_COLOR);
  };

  // ── Row ⋯ menu plumbing ──────────────────────────────────────────────────────
  // Every non-primary row action lives in ONE menu per row, rendered INLINE under the row
  // (never a floating popover — the pane is ~320px wide) with only one open at a time.
  const hasMenuActions = (entries: RowMenuEntry[]) => entries.some(entry => entry !== 'sep');

  const dotsButton = (key: string, entries: RowMenuEntry[]) => (
    hasMenuActions(entries) ? (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); toggleRowMenu(key); }}
        style={dotsBtnStyle}
        title="More actions"
      >⋯</button>
    ) : null
  );

  const rowMenu = (key: string, entries: RowMenuEntry[]) => (
    openRowMenu === key && hasMenuActions(entries) ? (
      <div style={rowMenuStyle} onClick={(e) => e.stopPropagation()}>
        {entries.map((entry, index) => (entry === 'sep' ? (
          <div key={`sep-${index}`} style={rowMenuSepStyle} />
        ) : (
          <RowMenuItem
            key={entry.label}
            label={entry.label}
            title={entry.title}
            danger={entry.danger}
            onClick={() => { setOpenRowMenu(null); entry.onClick(); }}
          />
        )))}
      </div>
    ) : null
  );

  // ── POI leaf row (shared by all levels; padding/font tuned per caller) ────────
  const renderPOILeaf = (node: HierarchyNode, opts: { fontSize: string; iconSize: string; typeSize: string; padding: string; marginLeft?: string; removable?: boolean; menuKey: string }) => {
    // Every row here IS an asset, so it gets the has-asset branch of the shared element
    // vocabulary — the same items, in the same words, as the viewer's double-click menu.
    const entries: RowMenuEntry[] = node.poi
      ? buildElementActions(true, {
          onFocus: onFocusPOI ? () => focusPOI(node.poi) : undefined,
          onCopyId: () => copyAssetId(node.poi),
          onCreateTicket: onCreateTicketForPOI ? () => onCreateTicketForPOI(node.poi) : undefined,
          onViewTickets: onViewTicketsForPOI ? () => onViewTicketsForPOI(node.poi) : undefined,
          onAddIot: onAddIoTForPOI ? () => onAddIoTForPOI(node.poi) : undefined,
          onShowInDiagram: onShowPOIInDiagram ? () => onShowPOIInDiagram(node.poi) : undefined,
          onEditAsset: onEditPOI ? () => onEditPOI(node.poi) : undefined,
          onDeleteAsset: onDeletePOI ? () => onDeletePOI(node.poi) : undefined,
        }, { name: node.name, focusTarget, canWrite: canWriteSite })
          // The vocabulary decides WHAT the actions are; this pane decides how they look, and
          // the destructive one has to read as destructive here the way "Remove from room"
          // already does below.
          .map((action) => (action.kind === 'deleteAsset' ? { ...action, danger: true } : action))
      : [];
    if (opts.removable && manage && node.poi) {
      if (entries.length > 0) entries.push('sep');
      entries.push({
        label: 'Remove from room',
        title: `Remove "${node.name}" from this location`,
        danger: true,
        onClick: () => doRemovePOIFromLocation(node.poi),
      });
    }
    return (
      <div key={node.id} style={opts.marginLeft ? { marginLeft: opts.marginLeft } : undefined}>
        <div
          onClick={() => focusPOI(node.poi)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: opts.padding,
            cursor: onFocusPOI ? 'pointer' : 'default',
            borderRadius: '4px',
            fontSize: opts.fontSize,
            color: 'var(--dt-text-3)',
            contentVisibility: 'auto',
            containIntrinsicSize: '0 25px',
          } as React.CSSProperties}
          title={onFocusPOI ? ELEMENT_FOCUS_ACTIONS[focusTarget].rowTitle(`"${node.name}"`) : node.name}
        >
          <span style={{ fontSize: opts.iconSize }}>{node.poi ? typeIcon(node.poi) : '📍'}</span>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {node.name}
          </span>
          <span style={{ fontSize: opts.typeSize, color: 'var(--dt-text-3)', opacity: 0.7 }}>
            {node.poi ? poiType(node.poi) : ''}
          </span>
          {dotsButton(opts.menuKey, entries)}
        </div>
        {rowMenu(opts.menuKey, entries)}
      </div>
    );
  };

  const chevron = (id: string) => (
    <span style={{ fontSize: '10px', color: 'var(--dt-text-3)' }}>
      {isOpen(id) ? '▼' : '▶'}
    </span>
  );

  // ── Locations: storey → space → poi ──────────────────────────────────────────
  const renderLocations = () => filteredLocations.nodes.map(storeyNode => {
    // Resolve the tree node back to its `locations` row so per-node edit/delete can act on it.
    const storeyRow = manage ? storeyLocationFor(storeyNode) : undefined;
    const storeyMenuKey = `storey:${storeyNode.id}`;
    const storeyEntries: RowMenuEntry[] = [];
    if (onDrawRoom) {
      storeyEntries.push({
        label: 'Draw room here',
        title: `Draw a custom room in "${storeyNode.name}"`,
        onClick: () => onDrawRoom(storeyNode.id.replace(/^storey-/, '')),
      });
    }
    if (storeyRow?.is_custom && onEditCustomStorey) {
      storeyEntries.push({
        label: 'Edit storey',
        title: `Edit custom storey "${storeyNode.name}"`,
        onClick: () => onEditCustomStorey(storeyRow),
      });
    }
    if (storeyRow) {
      if (storeyEntries.length > 0) storeyEntries.push('sep');
      storeyEntries.push({
        label: 'Delete storey',
        title: `Delete storey "${storeyNode.name}" and everything under it`,
        danger: true,
        onClick: () => doDeleteLocation(storeyRow),
      });
    }
    return (
    <div key={storeyNode.id}>
      <div
        onClick={() => toggle(storeyNode.id)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 4px',
          cursor: 'pointer', borderRadius: '4px',
        }}
      >
        {chevron(storeyNode.id)}
        <span style={{ fontSize: '14px' }}>🏢</span>
        <span style={{
          flex: 1, fontSize: '12px', color: 'var(--dt-text)', fontWeight: '600',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{storeyNode.name}</span>
        <span style={countPillStyle}>{storeyNode.count}</span>
        {dotsButton(storeyMenuKey, storeyEntries)}
      </div>
      {rowMenu(storeyMenuKey, storeyEntries)}

      {isOpen(storeyNode.id) && (
        <div style={{ marginLeft: '16px' }}>
          {storeyNode.children.map(child => {
            const spaceRow = manage && child.type === 'space' ? spaceLocationFor(child, storeyRow) : undefined;
            const spaceMenuKey = `space:${storeyNode.id}/${child.id}`;
            const spaceEntries: RowMenuEntry[] = [];
            if (spaceRow) {
              spaceEntries.push({
                label: 'Add asset',
                title: `Add an asset to "${child.name}" (create new or assign existing)`,
                onClick: () => openAddAsset(spaceRow),
              });
              spaceEntries.push({
                label: 'Assign existing asset',
                title: `Assign an existing asset to "${child.name}"`,
                onClick: () => openAddAsset(spaceRow, 'assign'),
              });
            }
            if (child.isCustom && onEditCustom) {
              spaceEntries.push({
                label: 'Edit room geometry',
                title: `Edit custom room "${child.name}"`,
                onClick: () => onEditCustom(child),
              });
            }
            if (spaceRow) {
              if (spaceEntries.length > 0) spaceEntries.push('sep');
              spaceEntries.push({
                label: 'Delete room',
                title: `Delete location "${child.name}"`,
                danger: true,
                onClick: () => doDeleteLocation(spaceRow),
              });
            }
            return (
            child.type === 'space' ? (
              <div key={child.id} style={{ contentVisibility: 'auto', containIntrinsicSize: '0 35px' } as React.CSSProperties}>
                <div
                  onClick={() => toggle(child.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '4px',
                    cursor: 'pointer', borderRadius: '4px',
                  }}
                >
                  {chevron(child.id)}
                  <span style={{ fontSize: '12px' }}>📐</span>
                  <span style={{
                    flex: 1, fontSize: '11px', color: 'var(--dt-text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {child.name}
                    {child.isCustom && (
                      <span style={{
                        fontSize: '8px', marginInlineStart: '4px', padding: '1px 4px',
                        backgroundColor: 'rgba(144, 190, 109, 0.3)', color: '#90BE6D',
                        borderRadius: '3px', verticalAlign: 'middle',
                      }}>Custom</span>
                    )}
                  </span>
                  {!child.isCustom && (
                    <span style={countPillStyle}>{child.count}</span>
                  )}
                  {child.roomDbId && onHighlightRoom && (
                    <RowChip
                      label="Room"
                      title={`Highlight room "${child.name}" in 3D viewer`}
                      onClick={(e) => { e.stopPropagation(); onHighlightRoom(child); }}
                    />
                  )}
                  {child.isCustom && child.polygon && onHighlightCustomRoom && (
                    <RowChip
                      label="Room"
                      title={`Show custom room "${child.name}" in 3D viewer`}
                      onClick={(e) => { e.stopPropagation(); onHighlightCustomRoom(child); }}
                    />
                  )}
                  {onHighlightGroup && (
                    <RowChip
                      label="Assets"
                      title={`Highlight all ${child.count} assets in this space`}
                      onClick={(e) => { e.stopPropagation(); highlightGroup(child); }}
                    />
                  )}
                  {dotsButton(spaceMenuKey, spaceEntries)}
                </div>
                {rowMenu(spaceMenuKey, spaceEntries)}
                {isOpen(child.id) && (
                  <div style={{ marginLeft: '24px' }}>
                    {child.children.map(poiNode => renderPOILeaf(poiNode, {
                      fontSize: '11px', iconSize: '12px', typeSize: '9px', padding: '3px 4px',
                      removable: true, menuKey: `poi:${child.id}/${poiNode.id}`,
                    }))}
                  </div>
                )}
              </div>
            ) : (
              renderPOILeaf(child, {
                fontSize: '11px', iconSize: '12px', typeSize: '9px', padding: '3px 4px',
                marginLeft: '8px', menuKey: `poi:${storeyNode.id}/${child.id}`,
              })
            )
            );
          })}
        </div>
      )}
    </div>
    );
  });

  // ── Systems: category → subcategory → system → poi ───────────────────────────
  const renderSystems = () => filteredSystems.nodes.map(categoryNode => (
    <div key={categoryNode.id}>
      <div
        onClick={() => toggle(categoryNode.id)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 4px',
          cursor: 'pointer', borderRadius: '4px',
        }}
      >
        {chevron(categoryNode.id)}
        <span style={{ fontSize: '14px' }}>{categoryIcon(categoryNode.name)}</span>
        <span style={{
          flex: 1, fontSize: '12px', color: 'var(--dt-text)', fontWeight: '600',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{categoryNode.name}</span>
        <span style={countPillStyle}>{categoryNode.count}</span>
        {onOpenSystemDiagram && (
          <RowChip
            label="Diagram"
            title={`Open "${categoryNode.name}" as a diagram`}
            onClick={(e) => { e.stopPropagation(); onOpenSystemDiagram(categoryNode); }}
          />
        )}
        {onHighlightGroup && (
          <RowChip
            label="3D"
            title={`Highlight all ${categoryNode.count} assets in this system`}
            onClick={(e) => { e.stopPropagation(); highlightGroup(categoryNode); }}
          />
        )}
      </div>

      {isOpen(categoryNode.id) && (
        <div style={{ marginLeft: '16px' }}>
          {categoryNode.children.map(child => (
            child.type === 'subcategory' ? (
              <div key={child.id} style={{ contentVisibility: 'auto', containIntrinsicSize: '0 35px' } as React.CSSProperties}>
                <div
                  onClick={() => toggle(child.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', padding: '4px',
                    cursor: 'pointer', borderRadius: '4px',
                  }}
                >
                  {chevron(child.id)}
                  <span style={{ fontSize: '12px' }}>{subCategoryIcon(child.name)}</span>
                  <span style={{
                    flex: 1, fontSize: '11px', color: 'var(--dt-text)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{child.name}</span>
                  <span style={countPillStyle}>{child.count}</span>
                  {onHighlightGroup && (
                    <RowChip
                      label="3D"
                      title={`Highlight all ${child.count} POIs in this subcategory`}
                      onClick={(e) => { e.stopPropagation(); highlightGroup(child); }}
                    />
                  )}
                </div>
                {isOpen(child.id) && (
                  <div style={{ marginLeft: '20px' }}>
                    {child.children.map(subChild => (
                      subChild.type === 'system' ? (
                        <div key={subChild.id} style={{ contentVisibility: 'auto', containIntrinsicSize: '0 30px' } as React.CSSProperties}>
                          <div
                            onClick={() => toggle(subChild.id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 4px',
                              cursor: 'pointer', borderRadius: '4px',
                            }}
                          >
                            {chevron(subChild.id)}
                            <span style={{ fontSize: '11px' }}>📦</span>
                            <span style={{
                              flex: 1, fontSize: '10px', color: 'var(--dt-text)',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>{subChild.name}</span>
                            <span style={countPillStyle}>{subChild.count}</span>
                            {onHighlightGroup && (
                              <RowChip
                                label="3D"
                                title={`Highlight all ${subChild.count} POIs in this system`}
                                onClick={(e) => { e.stopPropagation(); highlightGroup(subChild); }}
                              />
                            )}
                          </div>
                          {isOpen(subChild.id) && (
                            <div style={{ marginLeft: '20px' }}>
                              {subChild.children.map(poiNode => renderPOILeaf(poiNode, {
                                fontSize: '10px', iconSize: '11px', typeSize: '8px', padding: '2px 4px',
                                menuKey: `poi:${subChild.id}/${poiNode.id}`,
                              }))}
                            </div>
                          )}
                        </div>
                      ) : (
                        renderPOILeaf(subChild, {
                          fontSize: '10px', iconSize: '11px', typeSize: '8px', padding: '2px 4px',
                          menuKey: `poi:${child.id}/${subChild.id}`,
                        })
                      )
                    ))}
                  </div>
                )}
              </div>
            ) : (
              renderPOILeaf(child, {
                fontSize: '11px', iconSize: '12px', typeSize: '9px', padding: '3px 4px',
                marginLeft: '8px', menuKey: `poi:${categoryNode.id}/${child.id}`,
              })
            )
          ))}
        </div>
      )}
    </div>
  ));

  // ── In-pane management forms ────────────────────────────────────────────────
  // Every form renders inline, stacked between the toolbar and the tree, so nothing here
  // introduces a second panel or a floating dialog. Confirmations reuse window.confirm(),
  // exactly as the pages do.
  const propertySelect = (
    value: string,
    onChange: (v: string) => void,
    placeholder: string,
  ) => (
    <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
      <option value="">{placeholder}</option>
      {/* Keep the current pick reachable even when the filter excludes it. */}
      {value && !filteredProperties.includes(value) && <option value={value}>{value}</option>}
      {filteredProperties.map(p => <option key={p} value={p}>{p}</option>)}
    </select>
  );

  const formActions = (label: string, onApply: () => void, disabled?: boolean) => (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
      <button onClick={closeForm} style={ghostBtn}>Cancel</button>
      <button onClick={onApply} disabled={!!disabled || !!busy} style={busyStyle(!!busy)}>{label}</button>
    </div>
  );

  const renderForm = () => {
    if (!form || !assetId) return null;

    if (form.kind === 'reassign-locations') {
      return (
        <div style={panelStyle}>
          <div style={labelStyle}>Reassign locations from properties</div>
          <input
            value={propertyFilter}
            onChange={e => setPropertyFilter(e.target.value)}
            placeholder="Filter properties…"
            style={inputStyle}
          />
          <div style={labelStyle}>Storey property</div>
          {propertySelect(form.storeyProperty || '', v => patchForm({ storeyProperty: v }), '— none —')}
          <div style={labelStyle}>Space (room) property</div>
          {propertySelect(form.spaceProperty || '', v => patchForm({ spaceProperty: v }), '— none —')}
          {formActions('Reassign', () => {
            if (!form.storeyProperty && !form.spaceProperty) {
              showErrorToast('Please select at least one property');
              return;
            }
            runAction('reassignLoc', async () => {
              const res = await runReassignLocations(assetId, {
                storeyProperty: form.storeyProperty,
                spaceProperty: form.spaceProperty,
              });
              return formatReassignResult(res);
            });
          })}
        </div>
      );
    }

    if (form.kind === 'reassign-systems') {
      const levels = form.subcategoryLevels || [''];
      return (
        <div style={panelStyle}>
          <div style={labelStyle}>Reassign systems from properties</div>
          <input
            value={propertyFilter}
            onChange={e => setPropertyFilter(e.target.value)}
            placeholder="Filter properties…"
            style={inputStyle}
          />
          <div style={labelStyle}>System property (top level, required)</div>
          {propertySelect(form.systemProperty || '', v => patchForm({ systemProperty: v }), '— select —')}
          {levels.map((level, index) => (
            <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={labelStyle}>Subcategory level {index + 1}</div>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  {propertySelect(level, v => {
                    const next = [...levels];
                    next[index] = v;
                    patchForm({ subcategoryLevels: next });
                  }, '— none —')}
                </div>
                {levels.length > 1 && (
                  <button
                    onClick={() => patchForm({ subcategoryLevels: levels.filter((_, i) => i !== index) })}
                    style={nodeDangerBtn}
                    title="Remove this level"
                  >✕</button>
                )}
              </div>
            </div>
          ))}
          <button
            onClick={() => patchForm({ subcategoryLevels: [...levels, ''] })}
            style={ghostBtn}
          >+ Add Subcategory Level</button>
          {formActions('Reassign', () => {
            if (!form.systemProperty) {
              showErrorToast('Please select a System property (top level)');
              return;
            }
            runAction('reassignSys', async () => {
              const res = await runReassignSystems(assetId, {
                systemProperty: form.systemProperty!,
                subcategoryLevels: levels,
              });
              return formatReassignResult(res);
            });
          })}
        </div>
      );
    }

    if (form.kind === 'create-location') {
      return (
        <div style={panelStyle}>
          <div style={labelStyle}>New location</div>
          <input
            value={form.name || ''}
            onChange={e => patchForm({ name: e.target.value })}
            placeholder="Location name"
            style={inputStyle}
          />
          <input
            value={form.description || ''}
            onChange={e => patchForm({ description: e.target.value })}
            placeholder="Description (optional)"
            style={inputStyle}
          />
          {formActions('Create', () => {
            if (!(form.name || '').trim()) { showErrorToast('Please enter a location name'); return; }
            runAction('createLoc', async () => {
              await runCreateLocation(assetId, { name: form.name!, description: form.description });
              showSuccessToast('Location created');
              return `Created "${form.name!.trim()}".`;
            });
          })}
        </div>
      );
    }

    if (form.kind === 'manual-storey') {
      return (
        <div style={panelStyle}>
          <div style={labelStyle}>New storey by elevation</div>
          <input
            value={form.name || ''}
            onChange={e => patchForm({ name: e.target.value })}
            placeholder="Storey name"
            style={inputStyle}
          />
          <div style={{ display: 'flex', gap: '6px' }}>
            <input
              value={form.bottom || ''}
              onChange={e => patchForm({ bottom: e.target.value })}
              placeholder="Bottom"
              type="number"
              style={inputStyle}
            />
            <input
              value={form.top || ''}
              onChange={e => patchForm({ top: e.target.value })}
              placeholder="Top"
              type="number"
              style={inputStyle}
            />
          </div>
          {formActions('Create', () => {
            const bottom = parseFloat(form.bottom || '');
            const top = parseFloat(form.top || '');
            if (!(form.name || '').trim() || isNaN(bottom) || isNaN(top) || bottom >= top) {
              showErrorToast('Enter a name and a bottom elevation below the top elevation');
              return;
            }
            runAction('manualStorey', async () => {
              await runSaveManualStorey(assetId, { name: form.name!, bottom, top });
              showSuccessToast('Storey created');
              return `Created storey "${form.name!.trim()}".`;
            });
          })}
        </div>
      );
    }

    if (form.kind === 'merge-storeys') {
      const selected = form.selected || [];
      return (
        <div style={panelStyle}>
          <div style={labelStyle}>Merge storeys ({selected.length} selected)</div>
          <div style={{
            maxHeight: '120px', overflowY: 'auto', border: '1px solid var(--dt-border)',
            borderRadius: '4px', padding: '4px', backgroundColor: 'var(--dt-surface)',
          }}>
            {storeyLocations.length === 0 && (
              <div style={{ fontSize: '10px', color: 'var(--dt-text-3)' }}>No storeys found.</div>
            )}
            {storeyLocations.map(storey => (
              <label key={storey.id} style={{
                display: 'flex', alignItems: 'center', gap: '5px',
                fontSize: '11px', color: 'var(--dt-text)', padding: '2px 0', cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={selected.includes(String(storey.id))}
                  onChange={() => {
                    const id = String(storey.id);
                    patchForm({
                      selected: selected.includes(id)
                        ? selected.filter(s => s !== id)
                        : [...selected, id],
                    });
                  }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{storey.name}</span>
              </label>
            ))}
          </div>
          <input
            value={form.name || ''}
            onChange={e => patchForm({ name: e.target.value })}
            placeholder="Merged storey name"
            style={inputStyle}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: 'var(--dt-text-2)' }}>
            <input
              type="checkbox"
              checked={!!form.destroyOrigins}
              onChange={e => patchForm({ destroyOrigins: e.target.checked })}
            />
            Delete the original storeys
          </label>
          {formActions('Merge', () => {
            if (selected.length < 2 || !(form.name || '').trim()) {
              showErrorToast('Select at least two storeys and enter a name');
              return;
            }
            runAction('merge', async () => {
              const storeys = storeyLocations.filter(s => selected.includes(String(s.id)));
              await runMergeStoreys(assetId, {
                name: form.name!, storeys, destroyOrigins: form.destroyOrigins,
              });
              showSuccessToast('Storeys merged');
              return `Merged ${storeys.length} storeys into "${form.name!.trim()}".`;
            });
          })}
        </div>
      );
    }

    if (form.kind === 'add-asset' && form.location) {
      const location = form.location;
      if (form.step === 'choice') {
        return (
          <div style={panelStyle}>
            <div style={labelStyle}>Add asset to “{location.name}”</div>
            <button onClick={() => patchForm({ step: 'create' })} style={toolBtn}>Create new asset</button>
            <button
              onClick={() => { patchForm({ step: 'assign' }); loadPoiOptions(); }}
              style={ghostBtn}
            >Assign existing asset</button>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={closeForm} style={ghostBtn}>Cancel</button>
            </div>
          </div>
        );
      }
      if (form.step === 'create') {
        return (
          <div style={panelStyle}>
            <div style={labelStyle}>New asset in “{location.name}”</div>
            <input
              value={form.name || ''}
              onChange={e => patchForm({ name: e.target.value })}
              placeholder="Asset name"
              style={inputStyle}
            />
            <input
              value={form.description || ''}
              onChange={e => patchForm({ description: e.target.value })}
              placeholder="Description (optional)"
              style={inputStyle}
            />
            <input
              value={form.type || ''}
              onChange={e => patchForm({ type: e.target.value })}
              placeholder="Type (default: Generic)"
              style={inputStyle}
            />
            <select
              value={form.category || 'structure'}
              onChange={e => patchForm({ category: e.target.value })}
              style={inputStyle}
            >
              {POI_CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {formActions('Create', () => {
              if (!(form.name || '').trim()) { showErrorToast('Please fill in all required fields'); return; }
              runAction('createPoi', async () => {
                await runCreatePOIInLocation(assetId, location, {
                  name: form.name!, description: form.description, type: form.type, category: form.category,
                });
                showSuccessToast('Asset created successfully!');
                return `Created "${form.name!.trim()}" in "${location.name}".`;
              });
            })}
          </div>
        );
      }
      return (
        <div style={panelStyle}>
          <div style={labelStyle}>Assign existing asset to “{location.name}”</div>
          <select
            value={form.existingId || ''}
            onChange={e => patchForm({ existingId: e.target.value })}
            style={inputStyle}
          >
            <option value="">{busy === 'pois' ? 'Loading assets…' : '— select an asset —'}</option>
            {poiOptions.map(poi => (
              <option key={String(poi.id)} value={String(poi.id)}>{poi.name || poi.title || poi.id}</option>
            ))}
          </select>
          {formActions('Assign', () => {
            const poi = poiOptions.find(p => String(p.id) === form.existingId);
            if (!poi) { showErrorToast('Please select an asset to assign'); return; }
            runAction('assignPoi', async () => {
              await runAssignExistingPOI(assetId, poi, location);
              showSuccessToast('Asset assigned successfully!');
              return `Assigned "${poi.name || poi.id}" to "${location.name}".`;
            });
          })}
        </div>
      );
    }

    return null;
  };

  // ── Manage sheet contents ────────────────────────────────────────────────────
  // Exactly the abilities the old toolbar exposed, grouped as the approved design draws them.
  // Legacy callbacks still win over the pane's built-ins, so no consumer ends up with two
  // buttons for one ability.
  const createActions: SheetAction[] = [];
  const assignActions: SheetAction[] = [];
  const systemActions: SheetAction[] = [];
  const dangerActions: SheetAction[] = [];

  if (tab === 'locations') {
    if (onDrawRoom) {
      createActions.push({
        label: '+ Room (draw)', title: 'Draw a custom room in the viewer',
        onClick: () => onDrawRoom(),
      });
    }
    if (onDrawStorey) {
      createActions.push({
        label: '+ Storey (draw)', title: 'Draw a custom storey in the viewer',
        onClick: () => onDrawStorey(),
      });
    }
    if (manage) {
      createActions.push({
        label: '+ Location', title: 'Create a new top-level location',
        onClick: () => { setResult(null); setForm({ kind: 'create-location', name: '', description: '' }); },
      });
      createActions.push({
        label: '+ Storey (elev.)', title: 'Create a storey from bottom/top elevations',
        onClick: () => { setResult(null); setForm({ kind: 'manual-storey', name: '', bottom: '', top: '' }); },
      });
    }

    if (onReassignLocations) {
      assignActions.push({
        label: busy === 'reassignLoc' ? 'Reassigning…' : 'Reassign locations',
        title: 'Re-run location assignment for all assets',
        disabled: busy === 'reassignLoc',
        onClick: () => runBusy('reassignLoc', onReassignLocations),
      });
    } else if (manage) {
      assignActions.push({
        label: 'Reassign locations',
        title: 'Re-run location assignment for all assets from element properties',
        onClick: openReassignLocations,
      });
    }

    if (onAssignByGeolocation) {
      assignActions.push({
        label: busy === 'geo' ? 'Assigning…' : 'Assign by geolocation',
        title: 'Assign assets to rooms by geolocation (uses room polygons)',
        disabled: busy === 'geo',
        onClick: () => runBusy('geo', onAssignByGeolocation),
      });
    } else if (manage) {
      assignActions.push({
        label: busy === 'geo' ? 'Assigning…' : 'Assign by geolocation',
        title: 'Assign assets to rooms by geolocation (extracts + caches room polygons first)',
        disabled: busy === 'geo',
        onClick: doGeolocate,
      });
    }

    if (onExportRoomsCSV) {
      assignActions.push({ label: 'Export rooms CSV', title: 'Export all rooms to CSV', onClick: onExportRoomsCSV });
    } else if (manage) {
      assignActions.push({ label: 'Export rooms CSV', title: 'Export all rooms to CSV', onClick: doExportRoomsCSV });
    }

    if (manage) {
      assignActions.push({
        label: 'Merge storeys', title: 'Merge two or more storeys into one spanning storey',
        onClick: () => { setResult(null); setForm({ kind: 'merge-storeys', name: '', selected: [], destroyOrigins: false }); },
      });
    }
    if (onOpenSearch) {
      assignActions.push({
        label: '🔍 Bulk edit', title: 'Search assets, Set Systems / Set Locations, bulk edit',
        onClick: onOpenSearch,
      });
    }

    if (manage) {
      dangerActions.push({
        label: busy === 'delAll' ? 'Deleting…' : 'Delete all locations',
        title: 'Delete ALL locations for this asset',
        disabled: busy === 'delAll',
        danger: true,
        onClick: doDeleteAllLocations,
      });
    }
  } else if (tab === 'systems') {
    if (onReassignSystems) {
      systemActions.push({
        label: busy === 'reassignSys' ? 'Reassigning…' : 'Reassign systems',
        title: 'Re-run system assignment for all assets',
        disabled: busy === 'reassignSys',
        onClick: () => runBusy('reassignSys', onReassignSystems),
      });
    } else if (manage) {
      systemActions.push({
        label: 'Reassign systems',
        title: 'Re-run system assignment for all assets from element properties',
        onClick: openReassignSystems,
      });
    }
    if (onOpenDiagrams) {
      systemActions.push({ label: '🔗 Diagram', title: 'Open the P&ID diagrams page', onClick: onOpenDiagrams });
    }
    if (onOpenSearch) {
      systemActions.push({
        label: '🔍 Bulk edit', title: 'Search assets, Set Systems / Set Locations, bulk edit',
        onClick: onOpenSearch,
      });
    }
  }

  const sheetGroups = tab === 'systems'
    ? [{ title: 'Systems', actions: systemActions }]
    : [{ title: 'Create', actions: createActions }, { title: 'Assign & export', actions: assignActions }];
  const hasManageActions = sheetGroups.some(group => group.actions.length > 0) || dangerActions.length > 0;

  const sheetButton = (action: SheetAction) => (
    <button
      key={action.label}
      onClick={action.onClick}
      disabled={action.disabled}
      title={action.title}
      style={{
        ...sheetBtnStyle,
        ...(action.danger ? { color: 'var(--dt-bad)', borderColor: 'var(--dt-bad)' } : {}),
        opacity: action.disabled ? 0.6 : 1,
        cursor: action.disabled ? 'wait' : 'pointer',
      }}
    >{action.label}</button>
  );

  const activeNodes = tab === 'systems' ? filteredSystems.nodes : filteredLocations.nodes;

  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Tabs: Locations | Systems | Model (Model only when the host supplies its sections) */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
        <button
          onClick={() => selectTab('locations')}
          style={tabBtnStyle(tab === 'locations', true)}
        >Locations</button>
        <button
          onClick={() => selectTab('systems')}
          style={tabBtnStyle(tab === 'systems', true)}
        >Systems</button>
        {viewerSections && (
          <button
            onClick={() => selectTab('model')}
            style={tabBtnStyle(tab === 'model', false)}
            title="Model files, loading, POI extraction, phase filter"
          >Model</button>
        )}
      </div>

      {/* Filter + Manage row — belongs to the asset tabs only; the Model tab has its own body. */}
      {tab !== 'model' && (
        <>
          <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
            <input
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Filter…"
              title="Filter this tree by name"
              style={filterInputStyle}
            />
            {hasManageActions && (
              <button
                onClick={() => setManageOpen(open => !open)}
                style={{ ...ghostBtn, whiteSpace: 'nowrap' }}
                title="Create, assign and export actions"
              >{manageOpen ? 'Manage ▴' : 'Manage ▾'}</button>
            )}
          </div>

          {/* Manage sheet — drops below the row, per tab */}
          {manageOpen && hasManageActions && (
            <div style={sheetStyle}>
              {sheetGroups.map(group => (group.actions.length === 0 ? null : (
                <div key={group.title}>
                  <div style={sheetTitleStyle}>{group.title}</div>
                  <div style={sheetWrapStyle}>{group.actions.map(sheetButton)}</div>
                </div>
              )))}
              {dangerActions.length > 0 && (
                <div style={{
                  borderTop: '1px solid var(--dt-border)', padding: '8px',
                  display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                }}>
                  <span style={{ flex: 1, fontSize: '9px', color: 'var(--dt-text-3)' }}>Irreversible</span>
                  {dangerActions.map(sheetButton)}
                </div>
              )}
            </div>
          )}

          {/* Inline management form (never a separate panel — it stacks inside this pane) */}
          {renderForm()}

          {/* Last action result / error — the pages show this inside their modals */}
          {result && (
            <div style={{
              marginBottom: '8px', padding: '6px 8px', borderRadius: '4px', fontSize: '10px',
              color: 'var(--dt-text)',
              backgroundColor: result.tone === 'good' ? 'var(--dt-good-t)' : 'var(--dt-bad-t)',
              border: `1px solid ${result.tone === 'good' ? 'var(--dt-good)' : 'var(--dt-bad)'}`,
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <span style={{ flex: 1 }}>{result.text}</span>
              <button
                onClick={() => setResult(null)}
                style={{ ...nodeBtn, border: 'none', color: 'var(--dt-text-3)' }}
                title="Dismiss"
              >✕</button>
            </div>
          )}
        </>
      )}

      {/* Model tab body — kept MOUNTED and toggled with display (exactly like the mockup's
          bpTab), so the viewer's model list/extraction state survives tab switches and renders
          unconditionally, with no collapsing, whenever the Model tab is active. */}
      {viewerSections && (
        <div style={{ display: tab === 'model' ? undefined : 'none' }}>
          {viewerSections}
        </div>
      )}

      {/* Tree body (Locations / Systems) */}
      {tab !== 'model' && (
        <div style={{
          backgroundColor: 'var(--dt-surface-2)', borderRadius: '6px', padding: '8px',
          maxHeight, overflowY: 'auto',
        }}>
          {!hasData ? (
            <div style={{ color: 'var(--dt-text-3)', fontSize: '12px', padding: '16px', textAlign: 'center' }}>
              {emptyMessage ?? 'No assets to show yet.'}
            </div>
          ) : activeNodes.length === 0 ? (
            <div style={{ color: 'var(--dt-text-3)', fontSize: '12px', padding: '16px', textAlign: 'center' }}>
              No matches for “{filterText.trim()}”.
            </div>
          ) : tab === 'locations' ? renderLocations() : renderSystems()}
        </div>
      )}
    </div>
  );
};

export default POIHierarchyBrowser;
